import { useMemo, useState } from 'react';
import {
  Button,
  Field,
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  Skeleton,
  SkeletonItem,
  TagGroup,
  Text,
  makeStyles,
  tokens,
  type TagGroupProps,
} from '@fluentui/react-components';
import { FIELD_LABEL_LAYOUT } from '@moko/ui';
import UserOrGroupSelector, {
  type IPermissionItem,
} from '../../common/selectors/UsersOrGroupSelectors';
import type { IGroup } from '../../services/groupService';

interface IGroupSelectorProps {
  groups: IGroup[];
  selectedIds: number[];
  onChange: (selectedIds: number[]) => void;
  label?: string;
  mandatory?: boolean;
  disabled?: boolean;
  loading?: boolean;
  validationMessage?: string;
}

const useStyles = makeStyles({
  root: {
    width: '100%',
    alignItems: 'start',
    '& .fui-Field__label': {
      marginInline: 0,
    },
    '& .fui-Field__control': {
      minWidth: 0,
      width: '100%',
    },
  },
  control: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.spacingHorizontalXS,
    width: '100%',
  },
  tagGroup: {
    display: 'flex',
    flexBasis: '100%',
    flexWrap: 'wrap',
    width: '100%',
    minWidth: 0,
    gap: tokens.spacingHorizontalXS,
  },
  emptyText: {
    color: tokens.colorNeutralForeground3,
  },
  pill: {
    borderRadius: tokens.borderRadiusCircular,
    flexGrow: 0,
    flexShrink: 0,
  },
  buttonBone: {
    borderRadius: tokens.borderRadiusMedium,
    flexGrow: 0,
    flexShrink: 0,
  },
  labelBone: {
    justifySelf: 'start',
  },
});

const GroupSelector = ({
  groups,
  selectedIds,
  onChange,
  label = 'Groups',
  mandatory = false,
  disabled = false,
  loading = false,
  validationMessage,
}: IGroupSelectorProps) => {
  const styles = useStyles();
  const [isOpen, setIsOpen] = useState(false);

  const groupsById = useMemo(() => {
    const map = new Map<number, IGroup>();
    for (const group of groups) map.set(group.id, group);
    return map;
  }, [groups]);

  const selectedGroups = useMemo(
    () =>
      selectedIds
        .map((id) => groupsById.get(id))
        .filter((group): group is IGroup => Boolean(group)),
    [selectedIds, groupsById]
  );

  const permissions = useMemo<IPermissionItem[]>(
    () =>
      selectedGroups.map((group) => ({
        id: group.id,
        isGroup: true,
        name: group.name,
      })),
    [selectedGroups]
  );

  const addGroup = (id: number, name?: string) => {
    if (selectedIds.includes(id)) return;
    if (name && !groupsById.has(id)) groupsById.set(id, { id, name } as IGroup);
    onChange([...selectedIds, id]);
  };

  const removeGroup = (id: number) => {
    onChange(selectedIds.filter((selectedId) => selectedId !== id));
  };

  const dismissGroup: TagGroupProps['onDismiss'] = (_event, data) => {
    removeGroup(Number(data.value));
  };

  return (
    <Field
      className={styles.root}
      label={
        loading ? (
          <SkeletonItem
            className={styles.labelBone}
            size={16}
            style={{ width: 52 }}
          />
        ) : (
          label
        )
      }
      required={!loading && mandatory}
      orientation="horizontal"
      style={FIELD_LABEL_LAYOUT}
      validationMessage={validationMessage}
    >
      <div className={styles.control}>
        {loading ? (
          <>
            <Skeleton className={styles.tagGroup} aria-label="Loading groups">
              {[72, 104, 96, 132, 108, 120, 156, 148, 164].map((width) => (
                <SkeletonItem
                  key={width}
                  className={styles.pill}
                  size={32}
                  style={{ width }}
                />
              ))}
            </Skeleton>
            <SkeletonItem
              className={styles.buttonBone}
              size={24}
              style={{ width: 118 }}
            />
          </>
        ) : (
          <>
            {selectedGroups.length === 0 ? (
              <Text className={styles.emptyText} size={200}>
                No groups selected
              </Text>
            ) : (
              <TagGroup
                className={styles.tagGroup}
                onDismiss={dismissGroup}
                aria-label={label}
              >
                {selectedGroups.map((group) => (
                  <InteractionTag
                    key={group.id}
                    value={group.id.toString()}
                    appearance="brand"
                  >
                    <InteractionTagPrimary hasSecondaryAction={!disabled}>
                      {group.name}
                    </InteractionTagPrimary>
                    {!disabled && (
                      <InteractionTagSecondary aria-label="remove" />
                    )}
                  </InteractionTag>
                ))}
              </TagGroup>
            )}
            <Button
              appearance="secondary"
              size="small"
              onClick={() => setIsOpen(true)}
              disabled={disabled}
            >
              Select groups
            </Button>
          </>
        )}
      </div>

      <UserOrGroupSelector
        userOrGroupOnly="group"
        isOpen={isOpen}
        hideUsersAndGroupsSelector={() => setIsOpen(false)}
        onUserOrGroupSelected={(id, _isGroup, username) =>
          addGroup(id, username)
        }
        permissions={permissions}
        onRemovePermission={removeGroup}
      />
    </Field>
  );
};

export default GroupSelector;
